import {
  AppRootElementBlueprint,
  createExtension,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import {
  AppRootWrapperBlueprint,
  IconBundleBlueprint,
  SignInPageBlueprint,
  ThemeBlueprint,
  TranslationBlueprint,
} from '@backstage/plugin-app-react';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { UserListFilterPersister } from '../../components/userListFilter/persistedUserListFilter';
import { DatadogRumInit } from '../../components/DatadogRumInit';
import { MixpanelInit } from '../../components/MixpanelInit';
import { AnalyticsErrorBoundary } from '../../components/AnalyticsErrorBoundary';
import { AppSignInPage } from './AppSignInPage';
import { orgTranslations } from '../../translations/org';
import { arriveDarkAppTheme, arriveLightAppTheme } from './themes';
import { icons } from './icons';
import { appApiExtensions } from './apis';

// The app plugin ships built-in `light`/`dark` themes. The legacy `themes`
// option replaced them, so they are disabled here (same extension ids, so this
// overrides rather than conflicts) to leave exactly the two Arrive themes.
const disabledDefaultTheme = (name: 'light' | 'dark') =>
  createExtension({
    kind: 'theme',
    name,
    attachTo: { id: 'api:app/app-theme', input: 'themes' },
    disabled: true,
    output: [],
    factory: () => [],
  });

// `convertLegacyAppRoot` only carries the layout and routes across, so the
// remaining app-level pieces are re-declared here. AlertDisplay and
// OAuthRequestDialog are NFS built-ins and intentionally not repeated.
// The analytics wrapper renders only after sign-in (identityApi is needed by
// MixpanelInit); the error boundary also covers the sidebar rendered by the
// legacy layout beneath it.
//
// The framework renders this wrapper twice per page load (bootstrap tree, then
// the finalized tree after sign-in). MixpanelInit guards with a module-level
// `initStarted` flag (not an SDK internal), so the remount skips `mixpanel.init`
// and leaves readiness to the first mount's identify chain.
export const analyticsRootWrapper = AppRootWrapperBlueprint.make({
  name: 'analytics',
  params: {
    component: ({ children }) => (
      <>
        <MixpanelInit />
        <AnalyticsErrorBoundary>{children}</AnalyticsErrorBoundary>
      </>
    ),
  },
});

export const appModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    TranslationBlueprint.make({ params: { resource: orgTranslations } }),
    ThemeBlueprint.make({
      name: 'arrive-light',
      params: { theme: arriveLightAppTheme },
    }),
    ThemeBlueprint.make({
      name: 'arrive-dark',
      params: { theme: arriveDarkAppTheme },
    }),
    disabledDefaultTheme('light'),
    disabledDefaultTheme('dark'),
    IconBundleBlueprint.make({ name: 'custom', params: { icons } }),
    SignInPageBlueprint.make({
      params: { loader: async () => AppSignInPage },
    }),
    AppRootElementBlueprint.make({
      name: 'signals-display',
      params: { element: <SignalsDisplay /> },
    }),
    AppRootElementBlueprint.make({
      name: 'datadog-rum-init',
      params: { element: <DatadogRumInit /> },
    }),
    AppRootElementBlueprint.make({
      name: 'user-list-filter-persister',
      params: { element: <UserListFilterPersister /> },
    }),
    analyticsRootWrapper,
    ...appApiExtensions,
  ],
});
